/* Liens d’avis — détail · DS-01 library · DS-11 empty (Bible §18 · F-9 · audit v4 §2).
   Batch 14 (D-63): « Destination » is retired as a merchant word — a link is a
   « lien d’avis » everywhere, and this page is its detail. The routes do not
   move (`/destinations`): a URL is not a word the merchant reads.
   Batch 9: the merchant adds and removes links in Paramètres › Plateformes (« Demandes › Liens d’avis » until the
   08/10 fold of « Vos demandes » into the builder);
   here he sees what was checked, what a platform still owes him, and what a
   pause would touch.
   Rows are the seam’s own refs (`getDestinations()`), so a link created from a
   campaign step appears here like any other — same object, same checks.
   States: default (library) · ?etat=vide (the day-one empty)
   · ?p=<plateforme> — the deep link « Détail » uses from « Liens d’avis »
     (ruled 07/09): the page opens on that platform’s checks, with the list
     underneath and its row marked. `&u=<url>` picks one custom link among
     several, since a link of yours is identified by its address. */
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, EmptyState, ErrorState, Hint, KV, List, ListRow, Note, PageHeader, Pill, PlatformMark, useToast } from '@/ui'
import { PLATFORMS, removeDestination, platformLabel, type PlatformId } from '@/lib/campaign'
import { VOCAB } from '@/lib/vocab'
import { toastWhenSaved, writeMark } from '@/lib/writes'
import { ALL, useScope } from '@/lib/scope'
import { useDestinations, type Destination } from './api'
import { Chargement, CONTROLES, CONTROLES_LIEN, Etat } from './parts'

/** Where a row’s detail lives — one screen per family of checks.
 *  A link-only platform goes to « Règles & confirmations », not to the fiche
 *  screen: that one compares a GOOGLE listing against what is published today,
 *  and Trustpilot has no Google fiche to drift from (fixed 07/09). */
const DETAIL: Record<PlatformId, string> = {
  google: '/destinations/google',
  facebook: '/destinations/facebook',
  custom: '/destinations/lien',
  pagesjaunes: '/destinations/regles',
  trustpilot: '/destinations/regles',
  tripadvisor: '/destinations/regles',
}
const EST_PLATEFORME = (v: string | null): v is PlatformId =>
  !!v && PLATFORMS.some(x => x.id === v)

/** A row's detail is its STATE first, its platform second: what a merchant
 *  wants from a paused or disabled row is the screen that explains the pause,
 *  not the platform's ordinary checks. Closes the cross-frame nav item carried
 *  since 31/08 — before this, three screens were reachable by URL only. */
const detailPour = (i: Destination) =>
  i.tone === 'crit' ? '/destinations/desactivee'
    : i.tone === 'paused' ? '/destinations/suspendre'
    : DETAIL[i.platform]

export default function Destinations() {
  const { data, isPending, isError, refetch } = useDestinations()
  const [params] = useSearchParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const [scope] = useScope()

  if (isPending) return <Chargement />
  if (isError) return (
    <ErrorState what="Impossible de charger vos liens d’avis."
      lost="Votre connexion a peut-être sauté — rien n’a été perdu."
      action={<Button variant="primary" size="sm" onClick={() => refetch()}>Réessayer</Button>} />
  )

  /* D-31 · lists filter, objects never fork: in an establishment’s context this
     page shows that establishment’s links and nothing else. */
  const items = scope === ALL ? data.items : data.items.filter(i => i.locationId === scope)
  const d = { ...data, items, location: scope === ALL ? data.location : (items[0]?.place ?? data.location) }
  const vide = params.get('etat') === 'vide' || d.items.length === 0

  /* ---------- ?p= · the platform the merchant came for ---------- */
  const pParam = params.get('p')
  const uParam = params.get('u')
  const vise = EST_PLATEFORME(pParam) ? pParam : null
  const ligneVisee = vise
    ? d.items.find(i => i.platform === vise && (!uParam || i.url === uParam)) ?? null
    : null

  const retirer = (i: Destination) => {
    const mark = writeMark()
    removeDestination({ locationId: i.locationId ?? '', platform: i.platform, state: 'validee', label: i.name, url: i.url })
    /* the seam is localStorage: nothing on the wire tells the cache it moved (with an API, 2b-2: once the server answers) */
    qc.invalidateQueries({ queryKey: ['destinations'] })
    toastWhenSaved(mark, toast, `${i.name} retiré — les campagnes qui l’utilisaient passent à la plateforme suivante.`, () => { qc.invalidateQueries({ queryKey: ['destinations'] }) })
  }

  const entete = (
    <PageHeader
      crumb={<a href="#" onClick={e => { e.preventDefault(); nav('/parametres/plateformes') }}>Paramètres › Plateformes</a>}
      title={`Liens d’avis — détail · ${d.location}`}
      description={<>La page technique derrière vos liens d’avis&nbsp;: ce qui a été vérifié,
        ce qu’une plateforme vous demande encore, et ce qu’une mise en pause toucherait.
        <Hint label="Lien d’avis">{VOCAB.destination.sentence}</Hint></>}
      next={<>vos liens s’ajoutent et se retirent dans <a href="#" onClick={e => { e.preventDefault(); nav('/parametres/plateformes') }}>Paramètres › Plateformes</a>.</>}
      actions={vide ? undefined : <Button size="sm" variant="primary" onClick={() => nav('/destinations/ajouter')}>Ajouter un lien</Button>} />
  )

  /* ---------- DS-11 · the gain first, the guardrail as its reason ---------- */
  if (vide) return (
    <div className="page">
      {entete}
      <EmptyState
        title="Où vos avis vont atterrir."
        why="C’est vide parce qu’aucun lien n’a encore été ajouté. Si votre fiche Google est déjà liée à votre établissement, l’ajout prend une dizaine de secondes."
        action={<>
          <Button variant="primary" size="sm" onClick={() => nav('/parametres/plateformes')}>Ajouter ma fiche Google</Button>
          <Button variant="quiet" size="sm" onClick={() => nav('/destinations/ajouter')}>Ajouter une autre adresse</Button>
        </>}>
        <p>
          Un lien d’avis, c’est simplement l’endroit public où un client peut laisser
          son avis&nbsp;: votre fiche Google, votre page Facebook, ou une autre adresse.
          C’est ce qui fait grimper votre note là où vos futurs clients la regardent
          avant de choisir.
        </p>
        <p>
          Vous en ajoutez une, nous vérifions qu’elle fonctionne, et le parcours enverra
          vos clients dessus — <b>sans jamais conditionner leur cadeau à l’avis laissé</b>.
          C’est cette règle qui rend vos avis conformes, et donc utilisables.
        </p>
      </EmptyState>
    </div>
  )

  /* ---------- DS-01 · the library ---------- */
  return (
    <div className="page">
      {entete}

      {vise && (
        <Card>
          {/* not `.t-eyebrow` here: it uppercases, and a state chip should read
              as the word it is — « En pause », not « EN PAUSE » */}
          <p className="t-card" style={{ margin: '0 0 10px', display: 'flex', gap: 8, alignItems: 'center' }}>
            <PlatformMark platform={vise} icon={ligneVisee?.icon} size={20} />
            {ligneVisee ? ligneVisee.name : platformLabel(vise)}
            {ligneVisee && <Etat tone={ligneVisee.tone} label={ligneVisee.label} />}
          </p>
          {ligneVisee ? (
            <>
              <KV rows={[
                ['Lien', ligneVisee.url ?? 'celui de votre fiche'],
                [vise === 'custom' ? 'Les deux contrôles' : 'Les sept contrôles',
                  (vise === 'custom' ? CONTROLES_LIEN : CONTROLES).join(' · ')],
                ...(ligneVisee.place ? [['Établissement', ligneVisee.place] as [string, string]] : []),
                ['Ce que cette page ne dit pas',
                  'qu’un avis a été déposé, ni que la méthode est approuvée par la plateforme'],
              ]} />
              <div className="actions">
                <Button size="sm" variant="primary" onClick={() => nav(detailPour(ligneVisee))}>
                  {ligneVisee.tone === 'crit' ? 'Pourquoi elle est indisponible'
                    : ligneVisee.tone === 'paused' ? 'Ce que la pause touche'
                    : vise === 'google' ? 'Ce que Google autorise'
                    : vise === 'facebook' ? 'Lier une Page'
                    : vise === 'custom' ? 'Le lien et son icône'
                    : 'Règles & confirmations'}
                </Button>
                <Button size="sm" variant="quiet" onClick={() => nav('/parametres/plateformes')}>Retour à Paramètres › Plateformes</Button>
              </div>
            </>
          ) : (
            <>
              {/* one sentence for six platforms — « liée » would disagree with
                  « Lien personnalisé », so the noun carries the agreement */}
              <p className="f-help" style={{ margin: 0 }}>
                Aucun lien {platformLabel(vise)} pour {d.location} pour l’instant. Rien n’est
                cassé&nbsp;: une campagne qui l’invitait passe simplement à la plateforme suivante.
              </p>
              <div className="actions">
                <Button size="sm" variant="primary" onClick={() => nav('/parametres/plateformes')}>Ajouter ce lien</Button>
              </div>
            </>
          )}
        </Card>
      )}

      <Note tone="ok">
        Vos liens d’avis décident <b>où votre note publique progresse</b> — la page que vos
        futurs clients regardent avant de vous choisir. Chacune est vérifiée avant d’être utilisée.
      </Note>

      <List>
        {d.items.map(i => (
          <ListRow key={i.id} icon={<PlatformMark platform={i.platform} icon={i.icon} />}
            title={<>{i.name}{i.place ? <span className="f-help"> · {i.place}</span> : null}
              {ligneVisee?.id === i.id && <> <Pill tone="ink" mark="↑">celle que vous cherchiez</Pill></>}</>}
            meta={<>{i.url ?? 'lien de votre fiche'} · {i.meta}</>}
            end={<span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Etat tone={i.tone} label={i.label} />
              <Button size="sm" variant="quiet" onClick={() => nav(detailPour(i))}>Détail</Button>
              {i.linked && i.platform !== 'google' &&
                <Button size="sm" variant="quiet" onClick={() => retirer(i)}>Retirer</Button>}
            </span>} />
        ))}
      </List>

      <p className="f-help" style={{ margin: '14px 0 0' }}>
        Comment le trafic se répartit entre vos plateformes&nbsp;: la <b>part d’apparition</b> de
        chaque lien dans une campagne (Google 60&nbsp;% · Pages Jaunes 40&nbsp;%) et la
        <b> rotation par client</b> (chaque plateforme a sa fenêtre&nbsp;: Google 60&nbsp;j,
        Trustpilot 90&nbsp;j — jamais Google en boucle). Un clic sur un lien n’est jamais un
        avis&nbsp;: la note de votre fiche est la seule preuve.
        {' '}<a href="#" onClick={e => { e.preventDefault(); nav('/destinations/regles') }}>Règles &amp; confirmations</a>
        {d.pendingAcks > 0 && <> — <a href="#" onClick={e => { e.preventDefault(); nav('/destinations/confirmations') }}>
          <b>{d.pendingAcks} confirmation{d.pendingAcks > 1 ? 's' : ''}</b> en attente</a>.</>}
      </p>
      <p className="f-help" style={{ margin: '8px 0 0' }}>
        Un retour privé n’est pas un lien d’avis&nbsp;: c’est un message qui vous est adressé,
        lu dans <a href="#" onClick={e => { e.preventDefault(); nav('/messages?onglet=retours') }}>Messages</a> et
        jamais publié. Il se choisit sur une visite de vos campagnes — <a href="#" onClick={e => { e.preventDefault(); nav('/campagnes') }}>Vos campagnes</a>.
      </p>
    </div>
  )
}
